/** Where in the document something went wrong. Lines and columns count from 1. */
export interface MarkupPosition {
  line: number;
  col: number;
}

/**
 * Something the loader could not carry on past.
 *
 * The position is the whole point: markup is written by hand, most of what goes wrong with it is
 * a typo, and an error that cannot say where it happened makes the reader hunt. Every throw in
 * this extension carries one, and every message names what was expected rather than only what
 * was found.
 */
export class MarkupError extends Error {
  readonly line: number;
  readonly col: number;

  constructor(message: string, position: MarkupPosition) {
    super(`${message} (line ${position.line}, column ${position.col})`);
    this.name = "MarkupError";
    this.line = position.line;
    this.col = position.col;
  }
}

/**
 * Something the loader carried on past.
 *
 * An unknown attribute, an unreadable length, an enum spelled wrong: each of these has an
 * obvious safe reading, and taking it beats refusing to show a window because one attribute out
 * of sixty was mistyped. They are reported rather than swallowed, so nothing is silently
 * ignored — the default is a console warning, and an application that wants them collected
 * passes its own `onWarning`.
 */
export interface MarkupWarning extends MarkupPosition {
  message: string;
}

export type WarningSink = (warning: MarkupWarning) => void;

export const consoleWarnings: WarningSink = (w) => {
  console.warn(`webwin markup: ${w.message} (line ${w.line}, column ${w.col})`);
};

/** Turn a list of names into `"a", "b" or "c"`, for an error that has to suggest something. */
export function listNames(names: readonly string[], limit = 12): string {
  if (names.length === 0) return "none";
  const sorted = [...names].sort();
  const shown = sorted.slice(0, limit).map((n) => `"${n}"`);
  if (sorted.length > limit) return `${shown.join(", ")} and ${sorted.length - limit} more`;
  if (shown.length <= 1) return shown.join("");
  return `${shown.slice(0, -1).join(", ")} or ${shown[shown.length - 1]}`;
}
